import { useLang } from "@/context/LanguageContext";
import { t } from "@/data/translations";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { motion } from "framer-motion";

function PriceTable({ routes, title, note, lang }) {
  const rp = t.routesPage[lang];
  return (
    <div className="mb-12 sm:mb-16 last:mb-0">
      <h3 className="font-heading text-lg sm:text-xl md:text-2xl font-medium tracking-tight text-[#0A0B10] mb-2">{title}</h3>
      {note && <p className="text-xs sm:text-sm text-[#FF3B30] font-medium mb-4 sm:mb-6">{note}</p>}
      <div className="border-t-2 border-[#0A0B10]">
        <div className="flex justify-between py-3 sm:py-4 border-b border-[#0A0B10] text-[10px] sm:text-xs uppercase tracking-[0.15em] sm:tracking-[0.2em] font-semibold text-[#0A0B10]">
          <span>{rp.colRoute}</span>
          <span>{rp.colPrice}</span>
        </div>
        {routes.map((r, i) => (
          <div key={i} className="flex justify-between items-baseline gap-4 py-3.5 sm:py-5 border-b border-[#E5E5E5]">
            <span className="text-xs sm:text-sm text-[#0A0B10] min-w-0">{r.route}</span>
            <span className="text-xs sm:text-sm font-semibold text-[#FF3B30] whitespace-nowrap flex-shrink-0">{r.price}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function RoutesAndPrices() {
  const { lang } = useLang();
  const rp = t.routesPage[lang];
  const sp = t.servicePages[lang];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-16 md:py-24">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <p className="text-[10px] sm:text-xs uppercase tracking-[0.2em] font-semibold text-[#FF3B30] mb-3 sm:mb-4" data-testid="routes-overline">
          {lang === "cs" ? "CENIK" : "PRICING"}
        </p>
        <h1
          data-testid="routes-title"
          className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold tracking-tighter leading-none text-[#0A0B10] mb-3 sm:mb-4"
        >
          {rp.title}
        </h1>
        <p className="text-sm sm:text-base text-[#666666] leading-relaxed mb-8 sm:mb-12 max-w-2xl" data-testid="routes-subtitle">
          {rp.subtitle}
        </p>
      </motion.div>

      <Tabs defaultValue="passenger" data-testid="routes-tabs">
        <TabsList className="bg-[#F5F5F5] rounded-none border border-[#E5E5E5] p-1 mb-8 sm:mb-12 w-full sm:w-auto">
          <TabsTrigger
            value="passenger"
            data-testid="tab-passenger"
            className="rounded-none data-[state=active]:bg-[#0A0B10] data-[state=active]:text-white text-xs sm:text-sm font-medium px-3 sm:px-6 flex-1 sm:flex-none"
          >
            {rp.tabPassenger}
          </TabsTrigger>
          <TabsTrigger
            value="cargo"
            data-testid="tab-cargo"
            className="rounded-none data-[state=active]:bg-[#0A0B10] data-[state=active]:text-white text-xs sm:text-sm font-medium px-3 sm:px-6 flex-1 sm:flex-none"
          >
            {rp.tabCargo}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="passenger" data-testid="tab-content-passenger">
          <PriceTable routes={sp["business-car"].routes} title={rp.businessCarTitle} lang={lang} />
          <PriceTable routes={sp.minibus.routes} title={rp.minibusTitle} lang={lang} />
        </TabsContent>

        <TabsContent value="cargo" data-testid="tab-content-cargo">
          <PriceTable routes={sp.cargo.routes} title={rp.cargoTitle} note={rp.cargoNote} lang={lang} />
          <PriceTable routes={sp.pallet.routes} title={rp.palletTitle} note={rp.palletNote} lang={lang} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
